import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography, 
  Box,
  Grid, 
  TextField,
  Button,
  Avatar, 
  Link,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  CircularProgress,
  Collapse
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const Register = () => {
  const navigate = useNavigate();
  const { register, authError, clearAuthError } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'user',
    phone: '',
    address: '',
    businessName: '',
    businessDescription: '',
    businessCategory: '',
    customCategory: ''
  });
  
  const [businessCategories, setBusinessCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const { 
    name, 
    email, 
    password, 
    confirmPassword, 
    role,
    phone,
    address,
    businessName,
    businessDescription,
    businessCategory,
    customCategory
  } = formData;
  
  // Fetch business categories when component mounts
  useEffect(() => {
    const fetchCategories = async () => {
      if (role === 'business') {
        setLoadingCategories(true);
        try {
          const response = await api.get('/api/users/business-categories');
          setBusinessCategories(response.data || []);
          
          // Set default category if categories are available
          if (response.data && response.data.length > 0) {
            setFormData(prevState => ({
              ...prevState,
              businessCategory: response.data[0]
            }));
          }
        } catch (error) {
          console.error('Error fetching business categories:', error);
          // Fallback categories
          setBusinessCategories([
            'Barber Shop',
            'Hospital',
            'Auto Service Center', 
            'Dental Care Center', 
            'Fitness Studio', 
            'Hair & Style Salon', 
            'Legal Consultancy', 
            'Spa & Massage Center', 
            'Tech Repair Shop',
            'Restaurant',
            'Other'
          ]);
        } finally {
          setLoadingCategories(false);
        }
      }
    };
    
    fetchCategories();
  }, [role]);
  
  // Clear errors on mount
  useEffect(() => {
    setError('');
    clearAuthError();
  }, [clearAuthError]);
  
  // Update local error state when auth context error changes
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  
  const toggleShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };
  
  const clearLocalStorage = () => {
    console.log('Clearing all auth-related localStorage data');
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset any previous error
    setError('');
    
    // Check if passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Validate business data if signing up as business
    if (role === 'business' && !businessName) {
      setError('Business name is required');
      return;
    }
    
    if (role === 'business' && !businessCategory) {
      setError('Business category is required');
      return;
    }
    
    // If "Other" is selected but no custom category is provided
    if (role === 'business' && businessCategory === 'Other' && !customCategory.trim()) {
      setError('Please specify your business category');
      return;
    }
    
    setLoading(true);
    
    // Clear any existing tokens or auth data before attempting registration
    clearLocalStorage();
    
    try {
      const userData = {
        name,
        email,
        password,
        role,
        phone,
        address
      };
      
      // Add business details if registering as a business
      if (role === 'business') {
        // Use custom category if "Other" is selected
        const finalCategory = businessCategory === 'Other' ? customCategory.trim() : businessCategory;
        
        userData.businessDetails = {
          businessName: businessName.trim(),
          website: '',
          description: businessDescription?.trim() || '',
          category: finalCategory
        };
      }
      
      console.log('Submitting registration with data:', {
        ...userData,
        password: '[REDACTED]'
      });
      
      // Try registration with a small delay to ensure storage is cleared
      setTimeout(async () => {
        try {
      const success = await register(userData);
      if (success) {
        if (role === 'business') {
          navigate('/business/dashboard');
        } else {
          navigate('/dashboard');
        }
      } else {
            // If registration failed but no error was set in auth context
            if (!authError) {
        setError('Registration failed. Please check your information and try again.');
      }
          }
        } catch (regError) {
          console.error('Registration error caught:', regError);
          
          if (regError.message && regError.message.includes('Network Error')) {
            setError(
              'Cannot connect to server. Please check if the server is running and your internet connection is stable.'
            );
            
            // Show more helpful information in development
            if (process.env.NODE_ENV === 'development') {
              setError(
                'Cannot connect to server at http://localhost:5000. Please make sure your backend server is running (cd server && npm start).'
              );
            }
          } else {
            setError('Connection error. Please check your network and try again.');
          }
        }
        setLoading(false);
      }, 300);
    } catch (error) {
      console.error('Error during registration:', error);
      setError('Registration failed. Please try again later.');
      setLoading(false);
    }
  };
  
  return (
    <Container component="main" maxWidth="sm">
      <Paper 
        elevation={3} 
        sx={{ 
          mt: 8, 
          p: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center' 
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <PersonAddIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Sign up
        </Typography>
        
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3, width: '100%' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="role-select-label">Account Type</InputLabel>
                <Select
                  labelId="role-select-label"
                  id="role"
                  name="role"
                  value={role}
                  label="Account Type"
                  onChange={handleChange}
                >
                  <MenuItem value="user">Customer</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                autoComplete="name"
                name="name"
                required
                fullWidth
                id="name"
                label="Full Name"
                autoFocus
                value={name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="new-password"
                value={password}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={toggleShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                value={confirmPassword}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={toggleShowConfirmPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="phone"
                label="Phone Number"
                id="phone"
                value={phone}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="address"
                label="Address"
                id="address"
                value={address}
                onChange={handleChange}
              />
            </Grid>
            
            {/* Business Information (only shown if role is business) */}
            <Collapse in={role === 'business'} sx={{ width: '100%' }}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Business Information
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required={role === 'business'}
                    fullWidth
                    name="businessName"
                    label="Business Name"
                    id="businessName"
                    value={businessName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required={role === 'business'}>
                    <InputLabel id="business-category-label">Business Category</InputLabel>
                    <Select
                      labelId="business-category-label"
                      id="businessCategory"
                    name="businessCategory"
                      value={businessCategory}
                    label="Business Category"
                    onChange={handleChange}
                    >
                      {businessCategories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Custom Category field - only shown when "Other" is selected */}
                {businessCategory === 'Other' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      name="customCategory"
                      label="Specify Your Category"
                      id="customCategory"
                      value={customCategory}
                      onChange={handleChange}
                      placeholder="Enter your business category"
                    />
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="businessDescription"
                    label="Business Description"
                    id="businessDescription"
                    multiline
                    rows={3}
                    value={businessDescription}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Sign Up'}
          </Button>
          <Grid container justifyContent="flex-end">
            <Grid item>
              <Link component={RouterLink} to="/login" variant="body2">
                Already have an account? Sign in
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default Register; 